import { Link } from 'react-router-dom';
import { PanelCard, SortableTh, Chip, Track, PagedFooter } from '../../../components/ui';
import usePagination from '../../../lib/usePagination';
import Sparkline from '../../../components/charts/Sparkline';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import type { AcademicDashboardResponse, DashboardGradeRow } from '../../../api/types';
import { deltaChip, pct } from './format';

const COLS: SortColumn<DashboardGradeRow>[] = [
  { key: 'name', accessor: (g) => g.name },
  { key: 'students', accessor: (g) => g.students, type: 'number' },
  { key: 'avg', accessor: (g) => g.avg_score, type: 'number' },
  { key: 'pass', accessor: (g) => g.pass_rate, type: 'number' },
  { key: 'delta', accessor: (g) => g.delta_4w, type: 'number' },
];

export default function GradesCard({ d, className = '' }: { d: AcademicDashboardResponse; className?: string }) {
  const { rows, sort, toggle } = useSortableTable(d.grades, COLS, { key: 'name', dir: 'asc' });
  const pg = usePagination(rows, 10, `${sort?.key}${sort?.dir}`);
  return (
    <PanelCard className={className} title="Grades" actions={<Link to="/class-performance" className="text-[13px] font-semibold text-muted-2 no-underline hover:text-ink hover:underline">Class performance →</Link>}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead><tr className="border-b border-line">
            <SortableTh columnKey="name" sort={sort} onToggle={toggle}>Grade</SortableTh>
            <SortableTh columnKey="students" sort={sort} onToggle={toggle} align="right">Students</SortableTh>
            <SortableTh columnKey="avg" sort={sort} onToggle={toggle} align="right">Average</SortableTh>
            <SortableTh columnKey="pass" sort={sort} onToggle={toggle} className="w-[34%]">Pass rate</SortableTh>
            <SortableTh columnKey="delta" sort={sort} onToggle={toggle} align="right">Δ 4 wks</SortableTh>
            <th className="whitespace-nowrap px-2.5 pb-2.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Trend</th>
          </tr></thead>
          <tbody>
            {pg.pageItems.map((g) => {
              const c = deltaChip(g.delta_4w);
              return (
                <tr key={g.id} className="border-b border-hairline last:border-0 hover:bg-surface-2">
                  <td className="px-2.5 py-3 font-semibold"><Link to="/class-performance" className="text-ink no-underline hover:underline">{g.name}</Link></td>
                  <td className="px-2.5 py-3 text-right font-mono">{g.students}</td>
                  <td className="px-2.5 py-3 text-right font-mono">{pct(g.avg_score)}</td>
                  <td className="px-2.5 py-3"><span className="flex items-center gap-2.5"><Track value={g.pass_rate ?? 0} className="flex-1" label={`${g.name} pass rate`} /><span className="w-10 text-right font-mono">{pct(g.pass_rate, 0)}</span></span></td>
                  <td className="px-2.5 py-3 text-right"><Chip tone={c.tone}>{c.text}</Chip></td>
                  <td className="px-2.5 py-3"><Sparkline values={g.trend} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Grades pagination" className="mt-3" />
    </PanelCard>
  );
}
